import { z } from "zod";
import type { PurchasePromoCode } from "@/core/components/sites/shared/promo-codes/promoCodes";
import { AddOn } from "../rooms/add-ons/addOns.types";
import type { PriceBasis } from "../activities-and-products/activities.types";

// Re-exported so this module stays the single import point for the bookings
// response types, while the shape stays shared with promo-code reporting.
export type { PurchasePromoCode };

export enum ReservationStatus {
	Pending = "Pending",
	Confirmed = "Confirmed",
	Expired = "Expired",
	Cancelled = "Cancelled",
	Completed = "Completed",
}

export enum CancelationType {
	User = "User",
	Hotel = "Hotel",
}

export enum CancellationReason {
	Other = "Other",
	ConfirmationMissed = "ConfirmationMissed",
	BookedElsewhere = "BookedElsewhere",
	ForceMajeure = "ForceMajeure",
	GuestComplaint = "GuestComplaint",
	NoShow = "NoShow",
	PriceTooHigh = "PriceTooHigh",
	ServiceNotAvailable = "ServiceNotAvailable",
	InputError = "InputError",
	InvalidPayment = "InvalidPayment",
	TravelAgency = "TravelAgency",
	RequestedByGuest = "RequestedByGuest",
	Update = "Update",
	BookingAbandoned = "BookingAbandoned",
	RequestedByBooker = "RequestedByBooker",
}

export const CancellationFormSchema = z.object({
	cancelledReason: z.enum(CancellationReason),
	notes: z.string().max(500).optional(),
	by: z.enum(CancelationType),
});

export type CancellationFormValues = z.infer<typeof CancellationFormSchema>;

export type RoomBlockSnapshotAdmin = {
	roomBlockId: string;
	eventId: string | null;
	// JSONB snapshot of the block at booking time. `groupCode` is the public
	// code the guest may have used to reach the block (null for older
	// bookings created before groupCode tracking existed).
	roomBlock: {
		name?: string;
		groupCode?: string | null;
		[key: string]: unknown;
	};
	event: { name: string; path?: string; [key: string]: unknown } | null;
};

export type Room = {
	bookingId: string;
	roomId: string;
	roomName: string;
	shortTitle: string;
	rateId: string;
	rateName: string;
	quantity: number;
	adults: number;
	children: number;
	infants: number;
	nights: number;
	earlyCheckIn: boolean;
	lateCheckOut: boolean;
	checkInDate: string;
	checkOutDate: string;
	totalAmount: number;
	tax: number;
	rooms: number;
	status: ReservationStatus;
	refundAmount: number;
	dailyPrice: {
		date: string;
		pricePerNight: number;
		tax: number;
	}[];
	// Booking readers send `addonId` (not the catalog `id`). Bookings made before
	// addon-quantity don't send `quantity`; missing = 1.
	addons?: (AddOn & { addonId?: string; quantity?: number })[] | null;
	externalId?: string | null;
	origin: ReservationOrigin;
	// Per item: the usage row lives at booking level, so each room shows the
	// code that applied to IT, not a purchase-wide one.
	promoCode?: PurchasePromoCode | null;
	/**
	 * Snapshot completo del RoomBlock + Event al que pertenece este booking
	 * (Parte E §8.5). `null` cuando no es de room-block. Sólo se popula en
	 * los endpoints de admin (`getBookingsByProperty`,
	 * `getBookingDetailsByPurchaseId`). El nombre del evento se muestra como
	 * badge en `StayTable` cuando `roomBlock.event` no es null.
	 */
	roomBlock?: RoomBlockSnapshotAdmin | null;
};

export type Activity = {
	bookingId: string;
	activityId: string;
	activityName: string;
	activityDate: string;
	quantity: number;
	effectiveTime: string;
	additionalComments: string;
	attendees: number;
	tax: number;
	totalAmount: number;
	status: ReservationStatus;
	dailyPrice: {
		availableActivities: number;
		date: string;
		pricePerDay: number;
		tax: number;
		time: {
			quantity: number;
			time: string;
		};
	};
	promoCode?: PurchasePromoCode | null;
	// Activity snapshot fields (package-activity-axes). Optional because
	// bookings written before the full snapshot was stored on package rows,
	// and older standalone rows, do not carry them; readers render "—".
	unitName?: string | null;
	unitNamePlural?: string | null;
	// The activity validator's union; the formatter still guards against a
	// value outside it at runtime (see activityQuantityLabel).
	priceBasis?: PriceBasis | null;
	baseTitle?: string | null;
	extras?: ActivitySnapshotExtra[] | null;
};

// Shape of booking-service's activity snapshot `extras` entries.
export type ActivitySnapshotExtra = {
	name: string;
	quantity: number;
	price?: number;
};

export type Package = {
	id: string;
	activities: Activity[];
	room: Room;
	checkInDate: string;
	checkOutDate: string;
	detailPerDay: {
		date: string;
		roomName: string;
		activityName: string;
		adults: number;
		children: number;
		infants: number;
		totalAmount: number;
	}[];
	rateName: string;
	refundAmount: number;
	refundTaxAmount: number;
	status: ReservationStatus;
	totalAmount: number;
	tax: number;
	promoCode?: PurchasePromoCode | null;
};

export type Reservations = {
	bookings: Reservation[];
	pagination: {
		currentPage: number;
		totalPages: number;
	};
};
export type Reservation = {
	status: ReservationStatus;
	purchaseId: string;
	guestName: string;
	bookingDate: string;
	country: string;
	balance: number;
	totalAmount: number;
	tax: number;
	hotelBookingsCount: number;
	activityBookingsCount: number;
	packagesBookingsCount: number;
	hotelBookings: Room[];
	activityBookings: Activity[];
	packagesBookings: Package[];
	origin: string;
	externalId?: string | null;
	/**
	 * Resolved per purchase by a separate batch query in booking-service, so it
	 * is absent on responses from a backend that predates the feature — hence
	 * optional on top of nullable. Purchase details inherits it via
	 * ReservationDetails.
	 */
	// A LIST: usages live at booking level, so a multi-property purchase can
	// carry one code per property.
	promoCodes?: PurchasePromoCode[];
};

export enum ReservationOrigin {
	Mews = "Mews",
	Web = "Web",
}

export type ReservationDetails = Reservation & {
	initialAmount: number;
	initialTax: number;
	totalRefund: number;
	refundAmount: number;
	refundTaxAmount: number;
	guest: {
		city: {
			name: { id: string; name: string } | string;
			id: number;
		};
		name: string;
		email: string;
		phone: string;
		state: {
			name: { id: string; name: string } | string;
			id: number;
		};
		billingAddress: string;
		country: {
			name: string;
			isoCode: string;
		};
		lastName: string;
		billingPostalCode: string;
	};
	card: {
		address: string;
		type: string;
		token: string;
		number: string;
		expirationDate: number;
	};
};

export type ReservationStatusFilter = "Confirmed" | "Cancelled" | "Completed";
export type ReservationSourceFilter = "All" | "Web" | "Mews";
export type DateMode = "booking" | "checkIn" | "all";

export type ReservationFiltersValues = {
	purchaseId: string;
	guestName: string;
	promoCode: string;
	// Agents.id resolved by the modal's select (agentNumber alone is ambiguous).
	agentId?: string;
	status?: ReservationStatusFilter;
	source: ReservationSourceFilter;
	dateMode: DateMode;
	fromDate?: string;
	endDate?: string;
	checkInFrom?: string;
	checkInTo?: string;
};

export type ReservationsQuery = {
	siteId: string;
	token: string;
	page: number;
	limit: number;
	source: ReservationSourceFilter;
	dateMode: DateMode;
	fromDate?: string;
	endDate?: string;
	checkInFrom?: string;
	checkInTo?: string;
	status?: string;
	purchaseId?: string;
	guestName?: string;
	promoCode?: string;
	agentId?: string;
};
